import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { api, ApiError } from '../api/client';
import { evidenceUrl, investigatorBase as base } from '../api/investigator';
import ClipPlayer, { evidencePlayerKey } from '../components/ClipPlayer';
import { TRIM_MIN_SEC } from '../components/InvestigatorClip';
import InvestigatorSettings from '../components/InvestigatorSettings';
import { isSubstantive, oneLineSummary, parseSections } from '../util/parseDescription';
const active = (s) => s === 'queued' || s === 'running';
// Every time the reviewer reads is 12-hour store-local ("5:30 AM"); a date
// reads "Tue Oct 6". Numbers are epoch seconds, strings ISO timestamps.
const asDate = (value) => new Date(typeof value === 'number' ? value * 1000 : value);
export const clock = (value, tz) => asDate(value).toLocaleTimeString('en-US', { timeZone: tz ?? 'UTC', hour: 'numeric', minute: '2-digit', hour12: true }).replace(/\s/g, ' ');
export const dayOf = (value, tz) => { const p = new Intl.DateTimeFormat('en-US', { timeZone: tz ?? 'UTC', weekday: 'short', month: 'short', day: 'numeric' }).formatToParts(asDate(value)); const g = (t) => p.find(x => x.type === t)?.value ?? ''; return `${g('weekday')} ${g('month')} ${g('day')}`; };
const zoneName = (tz) => tz && tz !== 'UTC' ? ' · ' + tz : ' · UTC';
const stamp = (value, tz) => `${dayOf(value, tz)}, ${clock(value, tz)}`;
// A span names the day once when it stays on one calendar day.
export const span = (a, b, tz) => `${stamp(a, tz)} → ${dayOf(a, tz) === dayOf(b, tz) ? clock(b, tz) : stamp(b, tz)}`;
const zoneSec = (value, tz) => stamp(value, tz) + zoneName(tz);
export const KEEP_GOING = 'Keep going from where you stopped. Skip clips you already inspected and follow the leads you did not get to.';
const money = (n) => `$${Number(n).toFixed(4)}`;
// What the run actually cost us: settled provider spend plus unsettled usage.
// estimated_price is the marked-up price and is never the headline number.
// tracked_direct_cost (known_usd) and pending_cost (pending_usd) are disjoint sums (browser.py).
const directCost = (cost) => (Number(cost.tracked_direct_cost ?? 0) || 0) + (Number(cost.pending_cost ?? 0) || 0);
// The first sentence of a claim, bounded; `rest` is whatever the head leaves out.
export function firstSentence(text, limit = 160) {
    // Markdown markers are dropped: a **bold** span can straddle the sentence break.
    const t = text.replace(/\*\*/g, '').trim();
    const raw = (/^[\s\S]*?[.!?](?=\s|$)/.exec(t)?.[0] ?? t).trim();
    const head = raw.replace(/\s+/g, ' ');
    const shown = oneLine(head, limit);
    // Truncated: More carries only what the head has not shown yet.
    const after = t.slice(raw.length).trim();
    return { head: shown, rest: shown === head ? after : [head.slice(shown.length - 1).trim(), after].filter(Boolean).join(' ') };
}
// Defensive inline markdown (the model is told to write plain prose): **bold**,
// "- "/"* " list lines and blank-line paragraphs. Everything else stays a text
// node, so generated HTML or links render literally.
const inline = (t) => t.split(/\*\*(.+?)\*\*/g).map((x, i) => i % 2 ? _jsx("strong", { children: x }, i) : x);
export function Prose({ text, className }) {
    const out = [];
    text.trim().split(/\n\s*\n/).forEach((block, b) => {
        let para = [];
        let items = [];
        const flush = () => {
            if (para.length)
                out.push(_jsx("p", { children: inline(para.join('\n')) }, `${b}p${out.length}`));
            if (items.length)
                out.push(_jsx("ul", { children: items.map((x, i) => _jsx("li", { children: inline(x) }, i)) }, `${b}u${out.length}`));
            para = [];
            items = [];
        };
        for (const line of block.split('\n')) {
            const item = /^\s*[-*] (.*)$/.exec(line);
            if (item) {
                if (para.length)
                    flush();
                items.push(item[1]);
            }
            else {
                if (items.length)
                    flush();
                para.push(line);
            }
        }
        flush();
    });
    return _jsx("div", { className: 'inv-prose' + (className ? ' ' + className : ''), children: out });
}
const reasons = { timezone_changed: 'The store timezone changed. Review it and try again.', store_timezone_required: 'This store has no timezone set. Set it under the gear (Store & camera settings), then start again.', window_already_set: 'The time window is already set.', awaiting_window: 'Answer the time question first.', camera_access_changed: 'Camera access changed. Start a new investigation with your current cameras.', not_found: 'This investigation is no longer accessible.', store_unmapped: 'An operator must assign this store to a billing account before you can start.', queue_full: 'The investigation queue is full. Try again shortly.', provider_unavailable: 'The AI provider is temporarily unavailable. Try again shortly.', active_run: 'Wait for the current run to finish or cancel it first.', cancel_before_archive: 'Cancel the current run before archiving this investigation.', window_immutable: 'Follow-ups keep the original time window. Start a new investigation to change it.', idempotency_mismatch: 'This request changed during a retry. Reload the investigation before trying again.', metadata_conflict: 'Settings changed in another session. Reload the settings and try again.', metadata_busy: 'Settings are being updated. Try again shortly.', explicit_utc_required: 'This store has no timezone configured. Set it in Investigator settings before starting.', archived: 'Restore this investigation before asking a follow-up.', nonexistent_local_time: 'This local time does not exist because of daylight saving. Choose another time or its UTC offset.' };
const errorText = (e) => {
    if (!(e instanceof ApiError))
        return 'Connection interrupted. Check your connection and try again.';
    const detail = e.detail;
    if (detail.startsWith('ambiguous_local_time'))
        return 'This local time occurs twice because of daylight saving. Choose its UTC offset.';
    if (detail.startsWith('nonexistent_local_time'))
        return reasons.nonexistent_local_time;
    return reasons[detail.startsWith('active_run:') ? 'active_run' : detail] ?? (e.status === 401 ? 'Your session ended. Sign in again.' : e.status === 403 ? 'You do not have permission for this action.' : 'The request could not be completed. Try again.');
};
// Clip summaries reuse the shared describe parser so the reel, EventCard and
// StudioView all read a description the same way (it also tolerates the
// one-line scramble the describe model sometimes emits and strips frame
// references). Never fabricates text the description does not carry.
const clipSummary = (description) => {
    if (!description)
        return 'No description recorded.';
    const summary = oneLineSummary(description);
    if (summary)
        return summary;
    if (/FALL:\s*yes/i.test(description))
        return 'Possible fall detected.';
    return 'No notable activity described.';
};
const oneLine = (text, limit = 110) => {
    if (text.length <= limit)
        return text;
    const cut = text.lastIndexOf(' ', limit - 1);
    return text.slice(0, cut > 40 ? cut : limit - 1) + '…';
};
// Server detail `<reason>:choose_offset:-360,-300` lists the valid UTC offsets.
const offsetChoices = (e) => e instanceof ApiError && /^(ambiguous|nonexistent)_local_time/.test(e.detail) ? (e.detail.split('choose_offset:')[1] ?? '').split(',').filter(Boolean) : null;
const isSearch = (r) => (r.kind ?? 'search') === 'search';
const stateLabel = (s) => ({ queued: 'Queued', running: 'Investigating', completed: 'Completed', cancelled: 'Cancelled', timed_out: 'Time limit reached', failed: 'Run failed', budget_stopped: 'Cost limit reached', interrupted: 'Interrupted', queue_expired: 'Queue wait expired', auth_revoked: 'Access changed' }[s] ?? s.replaceAll('_', ' '));
// Intake runs read the report for its time window; they are never a search.
// intake.py closes an intake 'completed' with final_reason intake (window set,
// a search run follows in the same transaction) or needs_window[_unavailable]
// (then phase is awaiting_window, which the pill reports first).
const runLabel = (r, running) => !isSearch(r) && running ? 'Reading the report' : stateLabel(r.status);
const zoneTime = clock;
const elapsed = (seconds) => seconds < 60 ? `${seconds}s` : `${Math.floor(seconds / 60)}m ${String(seconds % 60).padStart(2, '0')}s`;
const clips = (n) => `${n} clip${n === 1 ? '' : 's'}`;
const humanize = (kind) => { const t = kind.replaceAll('_', ' '); return t.charAt(0).toUpperCase() + t.slice(1); };
// Intake system lines carry the intake's settled cost; `cause` stays internal.
const lineText = (kind, cost) => {
    const price = cost !== null && cost !== undefined && cost !== '' ? ` · $${Number(cost).toFixed(2)}` : '';
    return kind === 'window_asked' ? `Asked for the time window${price}` : kind === 'window_set' ? `Time window set${price}` : humanize(kind);
};
export const UNREADABLE_WINDOW = 'Could not read the time from the report; please choose it';
// F1 has no server-side conclusion yet (B2 adds it): the case summary is the
// first paragraph of the newest investigator message that has text.
export function conclusionOf(messages) {
    const last = [...messages].reverse().find(m => m.role !== 'user' && m.text.trim());
    return last ? last.text.trim().split(/\n\s*\n/)[0].trim() : '';
}
export const FULL_CLIPS_KEY = 'investigator.playFullClips';
// Skip to detections: start this long before the first marker, leave this long after the last.
const LEAD_SEC = 3, TAIL_SEC = 3;
export function trimWindow(markers) {
    if (!markers.length)
        return null;
    return { start: Math.max(0, markers[0].atSec - LEAD_SEC), end: markers.at(-1).atSec + TAIL_SEC };
}
// Early advance (skip to detections). Normal playback that reaches the trim
// end advances once, whatever the playhead step: Playback reports per rAF
// tick, but a throttled or backgrounded tab (and the native timeupdate) can
// step well over a second, which the old "step <= 1 s" rule read as a seek.
// A jump is told apart by the scrub flag instead: every seek-bar drag and
// marker click reports scrubbing until its landing, and a scrub disarms the
// gate until playback is seen below the end again. The gate stays armed
// while the settled length is unknown (fragmented tail still loading), so a
// crossing made before the length settles still advances once it does.
export function crossedTrimEnd(gate, sec, end, { scrubbing, playing, duration }) {
    // A scrub disarms even before an end exists: a playhead scrubbed past an
    // end adopted later must not read as playback reaching it.
    if (scrubbing) {
        gate.armed = false;
        return false;
    }
    if (end === null)
        return false;
    if (sec < end) {
        gate.armed = true;
        return false;
    }
    return gate.armed && playing && duration >= TRIM_MIN_SEC;
}
// Confidence in words; the raw level never reaches the reviewer.
export const CONFIDENCE_WORDS = { high: 'confident', medium: 'fairly confident', low: 'not confident' };
const confWord = (level) => CONFIDENCE_WORDS[level] ?? 'not confident';
const RANK = { high: 3, medium: 2, low: 1 };
// A clip's findings, the one to lead with first: any positive finding ahead
// of a ruled_out one (so a clip with a real finding is never shown dimmed),
// then highest confidence, then newest.
export function rankFindings(fs) {
    const t = (f) => f.created_at ? Date.parse(f.created_at) || 0 : 0;
    const out = (f) => f.category === 'ruled_out' ? 1 : 0;
    return fs.map((f, i) => ({ f, i })).sort((a, b) => out(a.f) - out(b.f) || (RANK[b.f.confidence] ?? 0) - (RANK[a.f.confidence] ?? 0) || t(b.f) - t(a.f) || b.i - a.i).map(x => x.f);
}
export const NO_FINDING = 'The investigator looked at this clip but recorded no finding.';
// The camera's own (auto) description as plain prose: section texts only.
export function cameraDescription(description) {
    if (!description)
        return '';
    const parts = parseSections(description).filter(x => isSubstantive(x.text, x.label === 'SCENE')).map(x => x.text.trim());
    return (parts.length ? parts.join(' ') : description).trim();
}
const byStart = (a, b) => Date.parse(a.event_start) - Date.parse(b.event_start);
// Where `s` would be inserted in the walk as ordered: before the first listed
// clip that starts after it, else at the end.
const slot = (list, s) => { const i = list.findIndex(x => byStart(x, s) > 0); return i < 0 ? list.length : i; };
// The one visibility rule for the rail. The backend returns inspected clips
// even without include_uncited, so the client splits: clips cited by a current
// finding and not thumbed down (hidden) form the main rail, everything else
// waits behind "Show the other clips". Both lists are in time order.
export function railSources(sources, findings) {
    const cited = new Set(findings.flatMap(f => f.citations.map(c => c.source_id)));
    const shown = (s) => cited.has(s.id) && !s.hidden;
    return { main: sources.filter(shown).sort(byStart), other: sources.filter(s => !shown(s)).sort(byStart) };
}
// Attaches each newly cited source to the latest investigator message written
// at or before its finding; without timestamps, to the newest investigator
// message. A finding older than every message stays unattached (pending row).
// Returns the same object when nothing changed.
export function attachCards(homes, messages, findings) {
    const ai = messages.filter(m => m.role !== 'user' && m.role !== 'system' && m.text).sort((a, b) => Date.parse(a.created_at) - Date.parse(b.created_at));
    if (!ai.length)
        return homes;
    let next = homes;
    for (const f of findings)
        for (const c of f.citations) {
            if (next[c.source_id])
                continue;
            const at = f.created_at ? Date.parse(f.created_at) : NaN;
            const home = Number.isNaN(at) || ai.some(m => Number.isNaN(Date.parse(m.created_at))) ? ai.at(-1) : ai.filter(m => Date.parse(m.created_at) <= at).at(-1);
            if (home) {
                if (next === homes)
                    next = { ...homes };
                next[c.source_id] = home.id;
            }
        }
    return next;
}
export default function InvestigateView() {
    const { user, selectedCustomer } = useAuth();
    const { id } = useParams();
    const store = user?.accessible_customers.find(c => c.slug === selectedCustomer);
    const navigate = useNavigate();
    const shown = useRef(store?.id);
    // Lives above the id-keyed Investigator so opening a saved investigation
    // from the drawer does not close the drawer.
    const [drawer, setDrawer] = useState(!id);
    // An open investigation belongs to one store. Switching stores in the top
    // bar moves to that store's investigations instead of a mismatch error.
    const switched = !!id && shown.current !== undefined && store !== undefined && shown.current !== store.id;
    useEffect(() => { if (store)
        shown.current = store.id; if (switched)
        navigate('/investigate', { replace: true }); }, [store?.id, switched]);
    if (switched)
        return _jsxs("div", { className: 'centered', children: ["Opening ", store.display_name, " investigations\u2026"] });
    if (!user || !store)
        return _jsx("div", { className: 'centered', children: "Select a store to investigate." });
    // A store/account switch destroys requests, streams, report text and media.
    return _jsx(Investigator, { userId: user.user_id, drawer: drawer, setDrawer: setDrawer, storeId: store.id, storeName: store.display_name, canEdit: store.role === 'admin' || user.is_global_admin, globalAdmin: user.is_global_admin }, `${user.user_id}:${store.id}:${id ?? "new"}`);
}
// Auto-open state machine (owner rule, ADR-194 F1):
// - Input: only live SSE snapshots of an active run on the default page. REST
//   reads (first load, paging, include_uncited) only mark findings as seen, so
//   findings that arrive while the reviewer is on another page never auto-open.
// - busy = walking (Play all) OR a chosen clip with media that has not yet
//   reported stopped OR scrubbing.
// - Idle: the newest unseen cited finding (by created_at) opens at once.
//   Busy: it is remembered (newest wins) as the deferred open.
// - The deferred open fires when the player reports stopped or a scrub ends,
//   while not walking and the run is still active. A pause during Play all
//   neither ends the walk nor opens it.
// - Play all ends only in advance() at the last clip (then the deferred open
//   may fire) or on a manual choose(), which also drops the deferred open.
// - Liveness: a chosen clip that never reports playing within 10 s stops
//   counting as busy.
// - Reports are tagged with the player's React key (evidencePlayerKey), so a
//   torn-down player's final "stopped" cannot affect the new clip.
function useInvestigatorData({ userId, storeId, storeName, canEdit, globalAdmin }) {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [items, setItems] = useState([]);
    const [next, setNext] = useState(null);
    const [archived, setArchived] = useState(false);
    const [nvrs, setNvrs] = useState([]);
    const [metadataEpoch, setMetadataEpoch] = useState(0);
    const [nvr, setNvr] = useState('');
    const [zone, setZone] = useState(null);
    const [zoneVersion, setZoneVersion] = useState(0);
    const [metadataLoaded, setMetadataLoaded] = useState(false);
    const [invZone, setInvZone] = useState(null);
    const [since, setSince] = useState('');
    const [until, setUntil] = useState('');
    const [fromOffset, setFromOffset] = useState('');
    const [toOffset, setToOffset] = useState('');
    // A timezone-changed restart prefills the report through router state.
    const [report, setReport] = useState(() => { const r = location.state?.report; return typeof r === 'string' ? r : ''; });
    const [needsOffset, setNeedsOffset] = useState(null);
    const [tzChanged, setTzChanged] = useState(false);
    const [data, setData] = useState(null);
    const [title, setTitle] = useState('');
    const [followup, setFollowup] = useState('');
    const [selection, setSelection] = useState(null);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [now, setNow] = useState(Date.now());
    const [messagePage, setMessagePage] = useState(0);
    const [sourcePage, setSourcePage] = useState(0);
    const [resolved, setResolved] = useState(null);
    const [includeUncited, setIncludeUncited] = useState(false);
    const [override, setOverride] = useState(null);
    const [pendingRun, setPendingRun] = useState(null);
    const [connectionEpoch, setConnectionEpoch] = useState(0);
    const [retryConnection, setRetryConnection] = useState(false);
    const citationGeneration = useRef(0);
    const walkAt = useRef(null);
    const [loadingClip, setLoadingClip] = useState(false);
    const [playing, setPlaying] = useState(false);
    const [playToken, setPlayToken] = useState(0);
    const pageQuery = `?message_page=${messagePage}&source_page=${sourcePage}&include_uncited=${includeUncited}`;
    const pageRef = useRef(pageQuery);
    pageRef.current = pageQuery;
    const reloadPage = useRef(null);
    const lifetime = useRef(new AbortController());
    const generation = useRef(0);
    const working = useRef(false);
    const requestKey = useRef(null);
    // Live player state from DragonPlayer, read by the auto-open rule.
    const mediaPlaying = useRef(false);
    const scrubbing = useRef(false);
    const seenFindings = useRef(null);
    // openNow: a finding that arrived while idle, opened after the snapshot
    // renders. deferredOpen: the newest finding that arrived while busy.
    const openNow = useRef(null);
    const deferredOpen = useRef(null);
    const liveSnapshot = useRef(() => { });
    const walkingRef = useRef(false);
    const playerKey = useRef('');
    const tokenRef = useRef(0);
    const liveness = useRef(null);
    const runActive = useRef(false);
    const [overrides, setOverrides] = useState({});
    const fbSeq = useRef(new Map());
    const fbConfirmed = useRef(new Map());
    const [fbErrors, setFbErrors] = useState({});
    const overridesRef = useRef(overrides);
    overridesRef.current = overrides;
    const moreButton = useRef(null);
    const composeRef = useRef(null);
    // Source id -> the investigator message its clip card first attached to.
    const [cardHome, setCardHome] = useState({});
    // Case playback skips to the detections unless the reviewer asks for full clips (per user).
    const fullKey = `${FULL_CLIPS_KEY}.${userId}`;
    const [fullClips, setFullState] = useState(() => { try {
        return localStorage.getItem(fullKey) === '1';
    }
    catch {
        return false;
    } });
    const setFullClips = (on) => { setFullState(on); try {
        if (on)
            localStorage.setItem(fullKey, '1');
        else
            localStorage.removeItem(fullKey);
    }
    catch { /* session-only */ } };
    const trimmedOut = useRef('');
    const endGate = useRef({ key: '', armed: false });
    const path = id ? `${base}/investigations/${encodeURIComponent(id)}` : '';
    const visible = data && data.id === id && data.store_id === storeId ? data : null;
    useEffect(() => {
        const controller = new AbortController();
        lifetime.current = controller;
        return () => { controller.abort(); generation.current++; };
    }, []);
    useEffect(() => {
        const controller = new AbortController();
        api.get(`${base}/stores/${storeId}/nvrs`, controller.signal).then(rows => { setNvrs(rows); setNvr(rows[0]?.id ?? ''); }).catch(e => { if (!controller.signal.aborted)
            setError(errorText(e)); });
        return () => controller.abort();
    }, [storeId]);
    useEffect(() => {
        setZone(null);
        setZoneVersion(0);
        setMetadataLoaded(false);
        if (!nvr)
            return;
        const controller = new AbortController();
        // Loaded even with a null timezone, so a windowless start reaches the 422 store_timezone_required path.
        api.get(`${base}/stores/${storeId}/nvrs/${nvr}/metadata`, controller.signal).then(m => { setZone(m.timezone.timezone); setZoneVersion(m.timezone.version ?? 0); setMetadataLoaded(true); }).catch(e => { if (!controller.signal.aborted)
            setError(errorText(e)); });
        return () => controller.abort();
    }, [storeId, nvr, metadataEpoch]);
    useEffect(() => {
        const controller = new AbortController();
        setItems([]);
        setNext(null);
        api.get(`${base}/investigations?store_id=${storeId}&archived=${archived}`, controller.signal)
            .then(r => { setItems(r.items); setNext(r.next_cursor); }).catch(e => { if (!controller.signal.aborted)
            setError(errorText(e)); });
        return () => controller.abort();
    }, [storeId, archived, id]);
    useEffect(() => {
        if (!id)
            return;
        const controller = new AbortController();
        let stream;
        let failures = 0;
        let serial = 0;
        let titleLoaded = false;
        setRetryConnection(false);
        const clear = () => { setData(null); setSelection(null); setResolved(null); citationGeneration.current++; openNow.current = null; deferredOpen.current = null; };
        const accept = (value, live = false) => {
            if (controller.signal.aborted)
                return;
            if (value.store_id !== storeId || value.id !== id) {
                clear();
                setError('This investigation belongs to another store.');
                stream?.close();
                return;
            }
            // A REST read (initial load, paging, include_uncited) only seeds the
            // seen set; a live snapshot is checked for new findings first.
            if (live)
                liveSnapshot.current(value);
            else {
                const seen = seenFindings.current ?? new Set();
                value.findings.forEach(f => seen.add(f.id));
                seenFindings.current = seen;
            }
            setData(value);
            setError('');
        };
        const read = async () => {
            const query = pageRef.current;
            const request = ++serial;
            try {
                const value = await api.get(path + query, controller.signal);
                if (request !== serial || query !== pageRef.current || controller.signal.aborted)
                    return;
                accept(value);
                if (!titleLoaded) {
                    setTitle(value.title);
                    titleLoaded = true;
                }
            }
            catch (e) {
                if (!controller.signal.aborted && request === serial) {
                    clear();
                    setError(errorText(e));
                    if (e instanceof ApiError && [401, 403, 404].includes(e.status)) {
                        stream?.close();
                        setRetryConnection(false);
                    }
                }
            }
        };
        reloadPage.current = () => void read();
        void read().then(() => {
            if (controller.signal.aborted)
                return;
            stream = new EventSource(`${path}/events`, { withCredentials: true });
            stream.addEventListener('snapshot', event => {
                if (controller.signal.aborted)
                    return;
                serial++;
                failures = 0;
                setRetryConnection(false);
                try {
                    const value = JSON.parse(event.data);
                    if (pageRef.current === '?message_page=0&source_page=0&include_uncited=false')
                        accept(value, true);
                    else
                        void read();
                }
                catch {
                    clear();
                    stream?.close();
                    setError('Invalid live update. Retry the connection.');
                    setRetryConnection(true);
                }
            });
            stream.addEventListener('revoked', () => { serial++; clear(); stream?.close(); setError('Access ended. Sign in again or contact your administrator.'); });
            const interrupted = () => {
                serial++;
                clear();
                failures++;
                setError('Connection interrupted. Reconnecting securely…');
                if (failures >= 3) {
                    stream?.close();
                    setRetryConnection(true);
                    setError('Live updates are unavailable. Retry the connection.');
                }
                // A bounded REST probe distinguishes expired authorization from a
                // temporary transport error. Its data never bypasses a failed stream.
                api.get(path, controller.signal).catch(e => {
                    if (!controller.signal.aborted && e instanceof ApiError && [401, 403, 404].includes(e.status)) {
                        stream?.close();
                        setRetryConnection(false);
                        setError(errorText(e));
                    }
                });
            };
            stream.addEventListener('unavailable', () => { interrupted(); stream?.close(); setRetryConnection(true); });
            stream.onerror = interrupted;
        });
        return () => { controller.abort(); stream?.close(); reloadPage.current = null; };
    }, [id, path, storeId, connectionEpoch]);
    useEffect(() => { reloadPage.current?.(); }, [pageQuery]);
    useEffect(() => () => { if (liveness.current)
        clearTimeout(liveness.current); }, []);
    useEffect(() => { const timer = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(timer); }, []);
    // A changed From/Until invalidates any offset entered for the old times.
    function invalidate() { setNeedsOffset(null); setFromOffset(''); setToOffset(''); }
    async function perform(action) {
        if (working.current)
            return;
        working.current = true;
        setBusy(true);
        setError('');
        const epoch = generation.current;
        try {
            await action();
        }
        catch (e) {
            if (!lifetime.current.signal.aborted && epoch === generation.current) {
                setError(errorText(e));
                if (e instanceof ApiError && e.detail === 'timezone_changed')
                    invalidate();
            }
        }
        finally {
            working.current = false;
            if (!lifetime.current.signal.aborted)
                setBusy(false);
        }
    }
    async function submit(e, follow = false, preset) {
        e?.preventDefault();
        const text = preset ?? (follow ? followup : report);
        // A new investigation starts without a window: the intake run asks for it (ADR-194 B3).
        const window = follow ? visible?.window : null;
        if ((follow && !window) || (!follow && (!nvr || !metadataLoaded)) || !text.trim())
            return;
        const request = window ? { store_id: storeId, nvr: visible.nvr_id, report: text, since: window.since, until: window.until, timezone_version: window.timezone_version } : { store_id: storeId, nvr, report: text, timezone_version: zoneVersion };
        const encoded = JSON.stringify({ path: follow ? path : 'create', ...request });
        if (requestKey.current?.body !== encoded)
            requestKey.current = { body: encoded, key: crypto.randomUUID() };
        const key = requestKey.current.key;
        const epoch = generation.current;
        // Captured before the POST: the new run may reach us by SSE before it resolves.
        const before = visible?.runs.at(-1)?.id ?? null;
        await perform(async () => {
            // Recorded before the POST so a run that starts (or even finishes) before
            // it resolves still releases the Conversation view.
            if (follow) {
                setOverride(null);
                setPendingRun({ before });
            }
            let result;
            try {
                result = await api.post(follow ? `${path}/runs` : `${base}/investigations`, { ...request, key }, lifetime.current.signal);
            }
            catch (e) {
                if (follow)
                    setPendingRun(null);
                else if (e instanceof ApiError && e.detail === 'timezone_changed')
                    setMetadataEpoch(n => n + 1);
                throw e;
            }
            if (lifetime.current.signal.aborted || generation.current !== epoch)
                return;
            requestKey.current = null;
            if (!preset)
                setFollowup('');
            if (!follow)
                navigate(`/investigate/${result.investigation_id}`);
        });
    }
    // Clip feedback (ADR-194 F3) is keyed by event: every loaded source of the
    // event shares it. A vote is shown at once through a per-event pending
    // override that survives live snapshots; per-event sequence numbers make the
    // newest intent win. Only the latest reply or failure for an event lands: a
    // reply is authoritative, a failure reverts to the last confirmed state.
    async function sendFeedback(s, verdict, note) {
        const ev = s.event_id;
        const n = (fbSeq.current.get(ev) ?? 0) + 1;
        fbSeq.current.set(ev, n);
        if (!fbConfirmed.current.has(ev))
            fbConfirmed.current.set(ev, { feedback: s.feedback ?? null, hidden: !!s.hidden });
        setOverrides(o => ({ ...o, [ev]: { feedback: { verdict, note }, hidden: verdict === 'down' } }));
        setFbErrors(m => { if (!(ev in m))
            return m; const { [ev]: _, ...rest } = m; return rest; });
        const settle = (p) => {
            setData(d => d && { ...d, sources: d.sources.map(x => x.event_id === ev ? { ...x, ...p } : x) });
            setResolved(r => r?.event_id === ev ? { ...r, ...p } : r);
            setOverrides(o => { const { [ev]: _, ...rest } = o; return rest; });
        };
        const live = () => !lifetime.current.signal.aborted && fbSeq.current.get(ev) === n;
        try {
            const r = await api.put(`${path}/sources/${encodeURIComponent(s.id)}/feedback`, { verdict, note });
            // A superseded reply is not confirmed state: a newer one is in flight or landed.
            if (live()) {
                fbConfirmed.current.set(ev, { feedback: r.feedback, hidden: r.hidden });
                settle(r);
            }
            return true;
        }
        catch (e) {
            if (live()) {
                settle(fbConfirmed.current.get(ev));
                const text = errorText(e);
                setFbErrors(m => ({ ...m, [ev]: text }));
            }
            return false;
        }
    }
    // The clarifying turn's answer. A duplicate answer means the window is
    // already set: refresh and carry on. A changed store timezone ends this
    // investigation; the banner offers a prefilled restart.
    async function answerWindow(body) {
        const epoch = generation.current;
        await perform(async () => {
            try {
                await api.post(`${path}/window`, body, lifetime.current.signal);
            }
            catch (e) {
                if (e instanceof ApiError && e.detail === 'window_already_set') {
                    answered();
                    return;
                }
                if (e instanceof ApiError && e.detail === 'timezone_changed') {
                    setTzChanged(true);
                    return;
                }
                // DST replies show only the inline offset prompt, not the banner.
                const offsets = offsetChoices(e);
                if (offsets) {
                    setNeedsOffset(offsets);
                    return;
                }
                throw e;
            }
            if (lifetime.current.signal.aborted || epoch !== generation.current)
                return;
            answered();
        });
    }
    function answered() { setNeedsOffset(null); setSince(''); setUntil(''); setFromOffset(''); setToOffset(''); reloadPage.current?.(); }
    // The snapshot carries no report field: the first user message is the report.
    const restart = () => navigate('/investigate', { state: { report: visible?.messages.find(m => m.role === 'user' && m.text)?.text ?? visible?.title ?? '' } });
    // Consume the prefill once so a reload or back navigation does not reapply it.
    useEffect(() => { if (location.state?.report !== undefined)
        navigate(location.pathname, { replace: true, state: null }); }, []);
    const askAbout = (text) => { setFollowup(f => text + f); setTimeout(() => composeRef.current?.focus()); };
    async function mutate(action) {
        const epoch = generation.current;
        await perform(async () => {
            if (action === 'delete')
                await api.del(path);
            else if (action === 'cancel')
                await api.post(`${path}/cancel`, {});
            else
                await api.patch(path, action === 'rename' ? { title } : { archive: !visible?.archived_at });
            if (lifetime.current.signal.aborted || epoch !== generation.current)
                return;
            if (action === 'delete') {
                setData(null);
                setSelection(null);
                navigate('/investigate');
            }
            else {
                if (action === 'archive')
                    setArchived(!visible?.archived_at);
                reloadPage.current?.();
            }
        });
    }
    const latest = visible?.runs.at(-1);
    const running = latest && active(latest.status);
    const withFb = (s) => overrides[s.event_id] ? { ...s, ...overrides[s.event_id] } : s;
    const sources = (visible?.sources ?? []).map(withFb);
    const selected = sources.find(s => s.id === selection?.source) ?? (resolved && resolved.id === selection?.source ? withFb(resolved) : undefined);
    // `walking` is only true while the Play-clips playlist drives the stage.
    // Every manual navigation drops out of it, so a clip the reviewer opened by
    // hand ends where it ends instead of jumping them to the next one.
    const choose = (source, offset = 0, { walking = false, auto = false, at } = {}) => {
        // Walk steps (Play all, advance, Prev/Next) pin their exact walk entry;
        // any other pick is located by position().
        walkAt.current = at ?? null;
        const request = ++citationGeneration.current;
        if (!walking) {
            setPlaying(false);
            walkingRef.current = false;
        }
        // A reviewer's own pick cancels any deferred auto-open. Play-state refs
        // are reset by the player itself: an unmounting player reports false.
        if (!walking && !auto)
            deferredOpen.current = null;
        // A new player key means a fresh autoplaying player: busy until that
        // player reports stopped. Reports from the outgoing player (its teardown
        // false) are ignored by key. Re-choosing the open clip keeps the player.
        const current = sources.find(s => s.id === source);
        const nextKey = evidencePlayerKey({ investigation: id, source, offset }, tokenRef.current);
        if (nextKey !== playerKey.current) {
            playerKey.current = nextKey;
            mediaPlaying.current = !!current?.media_available;
            scrubbing.current = false;
            if (liveness.current)
                clearTimeout(liveness.current);
            liveness.current = null;
            // Autoplay blocked or player stalled: do not hold findings hostage.
            if (current?.media_available)
                liveness.current = setTimeout(() => {
                    liveness.current = null;
                    if (playerKey.current === nextKey && !lifetime.current.signal.aborted) {
                        mediaPlaying.current = false;
                        openDeferred();
                    }
                }, 10000);
        }
        setSelection({ investigation: id, source, offset });
        setResolved(current ?? null);
        setLoadingClip(!current);
        if (!current)
            void api.get(`${path}/sources/${encodeURIComponent(source)}`, lifetime.current.signal).then(value => {
                if (!lifetime.current.signal.aborted && request === citationGeneration.current)
                    setResolved(value);
            }).catch(e => { if (!lifetime.current.signal.aborted && request === citationGeneration.current)
                setError(errorText(e)); })
                .finally(() => { if (!lifetime.current.signal.aborted && request === citationGeneration.current)
                setLoadingClip(false); });
    };
    const findingsBySource = new Map();
    for (const f of visible?.findings ?? [])
        for (const c of f.citations) {
            const list = findingsBySource.get(c.source_id) ?? [];
            if (!list.includes(f))
                list.push(f);
            findingsBySource.set(c.source_id, list);
        }
    for (const [k, list] of findingsBySource)
        findingsBySource.set(k, rankFindings(list));
    const rail = railSources(sources, visible?.findings ?? []);
    // Stage navigation, Play all and auto-advance walk exactly what the rail
    // shows, in time order, each clip from its start (the trim applies); the
    // "other clips" join the walk only once the reviewer reveals them.
    const playlist = includeUncited ? [...rail.main, ...rail.other] : rail.main;
    const position = (sel) => sel ? playlist.findIndex(s => s.id === sel.source) : -1;
    // A fresh play token re-keys the player, so pressing Play all restarts the
    // first clip even when it is already the open one and already finished.
    // A finding can cite a clip that is not on the visible source page. Those
    // would render nowhere at all, so they are listed separately and their
    // citation buttons still resolve the clip through the single-source route.
    const onPage = new Set(sources.map(s => s.id));
    const offPage = (visible?.findings ?? []).filter(f => f.citations.length && !f.citations.some(c => onPage.has(c.source_id)));
    // Steps to walk entry i. An entry that would reuse the open player bumps
    // the token so it remounts.
    const step = (i, walking, auto = false) => {
        if (evidencePlayerKey({ investigation: id, source: playlist[i].id, offset: 0 }, tokenRef.current) === playerKey.current) {
            tokenRef.current++;
            setPlayToken(tokenRef.current);
        }
        choose(playlist[i].id, 0, { walking, auto, at: i });
    };
    const playAll = () => { const first = playlist.findIndex(s => s.media_available); if (first >= 0) {
        setPlaying(true);
        walkingRef.current = true;
        tokenRef.current++;
        setPlayToken(tokenRef.current);
        choose(playlist[first].id, 0, { walking: true, at: first });
    } };
    // Auto-advance while walking the playlist, and in the Case view for any
    // clip (round-2 owner rule): the next clip in rail order, stopping at the end.
    // In the Conversation view a clip opened by hand ends where it ends.
    // Reads the walking ref, not `playing` state, so the order of the player's
    // stopped report and onEnded does not matter.
    const advance = () => {
        if (!walkingRef.current && mode !== 'case')
            return;
        // A clip hidden mid-walk continues from its place in the visible list.
        const from = index >= 0 ? index + 1 : virtual;
        // Not a manual pick: auto keeps any deferred auto-open pending.
        if (from >= 0)
            for (let i = from; i < playlist.length; i++) {
                if (playlist[i].media_available) {
                    step(i, walkingRef.current, true);
                    return;
                }
            }
        walkingRef.current = false;
        setPlaying(false);
        openDeferred();
    };
    runActive.current = !!running;
    const busyNow = () => walkingRef.current || mediaPlaying.current || scrubbing.current;
    liveSnapshot.current = value => {
        const seen = seenFindings.current;
        const last = value.runs.at(-1);
        if (!seen || !last || !active(last.status))
            return;
        const unseen = value.findings.map((f, i) => ({ f, i })).filter(({ f }) => !seen.has(f.id) && f.citations.length);
        const time = (f) => f.created_at ? Date.parse(f.created_at) : NaN;
        // Newest by created_at; array order breaks ties and covers missing stamps.
        const fresh = unseen.sort((a, b) => { const ta = time(a.f), tb = time(b.f); return Number.isNaN(ta) || Number.isNaN(tb) || ta === tb ? a.i - b.i : ta - tb; }).at(-1)?.f;
        if (!fresh)
            return;
        const target = { source: fresh.citations[0].source_id, offset: (fresh.citations[0].offset_ms ?? 0) / 1000 };
        if (busyNow())
            deferredOpen.current = target;
        else {
            openNow.current = target;
            deferredOpen.current = null;
        }
    };
    function openDeferred() {
        const target = deferredOpen.current;
        if (!target || busyNow() || !runActive.current || lifetime.current.signal.aborted)
            return;
        deferredOpen.current = null;
        choose(target.source, target.offset, { auto: true });
    }
    useEffect(() => {
        if (!visible)
            return;
        const ids = visible.findings.map(f => f.id);
        if (!seenFindings.current)
            seenFindings.current = new Set(ids);
        else
            ids.forEach(i => seenFindings.current.add(i));
        const open = openNow.current;
        openNow.current = null;
        if (open)
            choose(open.source, open.offset, { auto: true });
    }, [visible]);
    // Snapshot state is confirmed server state for events with no vote in flight.
    useEffect(() => { for (const s of visible?.sources ?? [])
        if (!overridesRef.current[s.event_id])
            fbConfirmed.current.set(s.event_id, { feedback: s.feedback ?? null, hidden: !!s.hidden }); }, [visible]);
    useEffect(() => { if (pendingRun && visible && (visible.runs.at(-1)?.id ?? null) !== pendingRun.before)
        setPendingRun(null); }, [visible, pendingRun]);
    // Card homes are assigned only on the newest message page, where the latest
    // investigator messages are; once assigned a card never moves.
    useEffect(() => {
        if (!visible || messagePage !== 0)
            return;
        const next = attachCards(cardHome, visible.messages, visible.findings);
        if (next !== cardHome)
            setCardHome(next);
    }, [visible, messagePage]);
    // Awaiting the window, or with only an intake run finished, there is no case yet.
    const awaiting = visible?.phase === 'awaiting_window';
    const finished = !!visible && !running && !awaiting && visible.runs.some(r => isSearch(r) && !active(r.status));
    const mode = awaiting || !visible?.runs.some(isSearch) ? 'working' : override ?? (pendingRun !== null ? 'working' : finished ? 'case' : 'working');
    const invNvr = visible && !visible.window ? visible.nvr_id : '';
    useEffect(() => {
        setInvZone(null);
        if (!invNvr || invNvr === nvr)
            return;
        const controller = new AbortController();
        api.get(`${base}/stores/${storeId}/nvrs/${invNvr}/metadata`, controller.signal).then(m => setInvZone(m.timezone.timezone)).catch(() => { });
        return () => controller.abort();
    }, [invNvr, nvr, storeId]);
    const tz = visible?.window?.timezone ?? (invNvr && invNvr !== nvr ? invZone : zone);
    const pinned = walkAt.current;
    const index = pinned !== null && pinned < playlist.length && selection && playlist[pinned].id === selection.source ? pinned : position(selection);
    // An open clip outside the walk (hidden after opening): where its start time would sit.
    const virtual = index >= 0 || !selected ? -1 : slot(playlist, selected);
    const keyNow = selection ? evidencePlayerKey(selection, playToken) : '';
    const onPlayingChange = (key, value) => {
        if (key !== playerKey.current)
            return;
        if (liveness.current) {
            clearTimeout(liveness.current);
            liveness.current = null;
        }
        mediaPlaying.current = value;
        if (!value)
            openDeferred();
    };
    const onScrubChange = (key, value) => { if (key !== playerKey.current)
        return; scrubbing.current = value; if (!value)
        openDeferred(); };
    // Skip to detections: advance early, once per player, when normal playback
    // reaches the last detection (+3 s) of a clip whose settled length is 20 s
    // or more (crossedTrimEnd). Applies however the clip was opened, including
    // from the rail at a cited offset.
    const onTimeUpdate = (key, sec, duration, end) => {
        if (key !== playerKey.current || trimmedOut.current === key)
            return;
        if (endGate.current.key !== key)
            endGate.current = { key, armed: false };
        if (crossedTrimEnd(endGate.current, sec, end, { scrubbing: scrubbing.current, playing: mediaPlaying.current, duration })) {
            trimmedOut.current = key;
            advance();
        }
    };
    return { id, navigate, storeId, storeName, canEdit, globalAdmin, items, setItems, next, setNext, archived, setArchived, nvrs, nvr, setNvr, zone, since, setSince, until, setUntil, fromOffset, setFromOffset, toOffset, setToOffset,
        report, setReport, needsOffset, visible, zoneVersion, metadataLoaded, tz, awaiting, finished, tzChanged, answerWindow, restart, title, setTitle, followup, setFollowup, selection, error, busy, confirmDelete, setConfirmDelete, now, messagePage, setMessagePage, sourcePage, setSourcePage,
        includeUncited, setIncludeUncited, retryConnection, setConnectionEpoch, loadingClip, playing, playToken, path, lifetime, invalidate, setMetadataEpoch, perform, submit, mutate, sendFeedback, fbErrors, askAbout, composeRef,
        latest, running: !!running, pendingRun, sources, selected, index, virtual, playlist, step, rail, choose, findingsBySource, offPage, playAll, advance, mode, setOverride, cardHome, onPlayingChange, onScrubChange, onTimeUpdate, fullClips, setFullClips, moreButton, keyNow };
}
function Investigator({ drawer, setDrawer, ...props }) {
    const c = useInvestigatorData(props);
    const v = c.visible;
    // The banner shows the general error plus any per-event feedback failures.
    const errors = [...new Set([c.error, ...Object.values(c.fbErrors)].filter(Boolean))];
    const [settings, setSettings] = useState(false);
    return _jsxs("div", { className: 'investigator-layout' + (drawer ? '' : ' drawer-closed'), children: [drawer && _jsx(Drawer, { c: c }), _jsxs("section", { className: 'investigator-main', children: [_jsx(Header, { c: c, drawer: drawer, setDrawer: setDrawer, toggleSettings: () => setSettings(o => !o) }), settings && _jsx("div", { className: 'inv-settings-panel', children: _jsx(InvestigatorSettings, { initiallyOpen: true, storeId: c.storeId, nvr: v?.nvr_id ?? c.nvr, canEdit: c.canEdit, globalAdmin: c.globalAdmin, onUpdated: () => { c.invalidate(); c.setMetadataEpoch(n => n + 1); } }, v?.nvr_id ?? c.nvr) }), errors.length > 0 && _jsx("p", { role: 'alert', className: 'investigator-error', children: errors.join(' ') }), c.retryConnection && _jsx("button", { className: 'ghost', onClick: () => c.setConnectionEpoch(n => n + 1), children: "Retry connection" }), c.confirmDelete && _jsx(DeleteDialog, { c: c }), !c.id ? _jsx(StartForm, { c: c }) : !v ? _jsx("p", { role: 'status', children: c.error ? 'Private content is hidden until access is verified.' : 'Loading private investigation…' }) : c.mode === 'case' ? _jsx(CaseView, { c: c }) : _jsx(WorkingView, { c: c })] })] });
}
// Closes a popover on Escape or a click outside `ref`.
function useDismiss(open, close, ref) {
    useEffect(() => {
        if (!open)
            return;
        const key = (e) => { if (e.key === 'Escape')
            close(); };
        const down = (e) => { if (ref.current && !ref.current.contains(e.target))
            close(); };
        document.addEventListener('keydown', key);
        document.addEventListener('mousedown', down);
        return () => { document.removeEventListener('keydown', key); document.removeEventListener('mousedown', down); };
    }, [open]);
}
function DeleteDialog({ c }) {
    const cancel = useRef(null);
    const box = useRef(null);
    useEffect(() => {
        cancel.current?.focus();
        const key = (e) => {
            if (e.key === 'Escape') {
                c.setConfirmDelete(false);
                return;
            }
            if (e.key !== 'Tab' || !box.current)
                return;
            const items = [...box.current.querySelectorAll('button:not([disabled])')];
            if (!items.length)
                return;
            const at = items.indexOf(document.activeElement);
            e.preventDefault();
            items[(at + (e.shiftKey ? -1 : 1) + items.length) % items.length].focus();
        };
        document.addEventListener('keydown', key);
        // The menu item that opened the dialog is gone; return focus to the ⋯ button.
        return () => { document.removeEventListener('keydown', key); c.moreButton.current?.focus(); };
    }, []);
    return _jsxs("div", { ref: box, role: 'alertdialog', "aria-modal": 'true', "aria-label": 'Delete investigation', className: 'inv-notice', children: [_jsx("p", { children: "Delete this private report and its citations? Running work will be cancelled. Minimal usage accounting is retained." }), _jsx("button", { className: 'primary', disabled: c.busy, onClick: () => void c.mutate('delete'), children: "Delete permanently" }), " ", _jsx("button", { ref: cancel, className: 'ghost', onClick: () => c.setConfirmDelete(false), children: "Keep investigation" })] });
}
function Drawer({ c }) {
    return _jsxs("aside", { className: 'investigator-sidebar', children: [_jsx("button", { className: 'primary', onClick: () => c.navigate('/investigate'), children: "+ New investigation" }), _jsx("nav", { "aria-label": 'Saved investigations', children: c.items.map(item => _jsxs("button", { disabled: item.accessible === false, title: item.accessible === false ? 'Camera or NVR access changed. Start a new investigation.' : undefined, className: item.id === c.id ? 'selected' : '', onClick: () => c.navigate(`/investigate/${item.id}`), children: [item.title, _jsxs("small", { children: [item.nvr_id, " \u00B7 ", new Date(item.created_at).toLocaleDateString()] })] }, item.id)) }), !c.items.length && _jsx("p", { children: "No saved investigations." }), c.next && _jsx("button", { className: 'ghost', disabled: c.busy, onClick: () => void c.perform(async () => {
                    const r = await api.get(`${base}/investigations?store_id=${c.storeId}&archived=${c.archived}&after=${c.next}`, c.lifetime.current.signal);
                    if (!c.lifetime.current.signal.aborted) {
                        c.setItems(prev => [...prev, ...r.items]);
                        c.setNext(r.next_cursor);
                    }
                }), children: "Load more" }), _jsxs("label", { className: 'investigator-check inv-drawer-foot', children: [_jsx("input", { type: 'checkbox', checked: c.archived, onChange: e => c.setArchived(e.target.checked) }), " Archived sessions"] })] });
}
function Header({ c, drawer, setDrawer, toggleSettings }) {
    const v = c.visible;
    const [menu, setMenu] = useState(false);
    const menuRef = useRef(null);
    useDismiss(menu, () => setMenu(false), menuRef);
    return _jsxs("header", { className: 'inv-head', children: [_jsx("button", { className: 'ghost inv-icon', "aria-label": drawer ? 'Hide saved investigations' : 'Show saved investigations', "aria-expanded": drawer, onClick: () => setDrawer(!drawer), children: "\u2630" }), !v ? _jsxs("div", { className: 'inv-head-title', children: [_jsx("h1", { children: "Investigate" }), _jsxs("small", { children: ["Private investigations \u00B7 ", c.storeName] })] }) :
                _jsxs("div", { className: 'inv-head-title', children: [_jsxs("div", { className: 'inv-title-row', children: [_jsxs("label", { className: 'investigator-title', children: [_jsx("span", { className: 'inv-sr', children: "Session title" }), _jsx("input", { value: c.title, maxLength: 200, onChange: e => c.setTitle(e.target.value) })] }), c.title !== v.title && _jsx("button", { className: 'ghost', disabled: c.busy || !c.title.trim(), onClick: () => void c.mutate('rename'), children: "Save title" })] }), _jsxs("div", { className: 'inv-meta', children: [v.nvr_id, " \u00B7 ", v.window ? _jsxs(_Fragment, { children: [span(v.window.since, v.window.until, c.tz), zoneName(c.tz)] }) : 'Time window: not set yet', " ", c.mode === 'case' && _jsx(StatusPill, { c: c })] }), c.mode === 'case' && _jsx(Conclusion, { text: caseConclusion(v) })] }), _jsxs("div", { className: 'inv-head-actions', children: [v && c.mode === 'case' && _jsx(StopButton, { c: c }), v && _jsxs("div", { className: 'inv-seg', role: 'group', "aria-label": 'View', children: [_jsx("button", { "aria-pressed": c.mode === 'case', disabled: c.awaiting || !v.runs.some(isSearch), onClick: () => c.setOverride('case'), children: "Case" }), _jsxs("button", { "aria-pressed": c.mode === 'working', onClick: () => c.setOverride('working'), children: ["Conversation \u00B7 ", v.messages.filter(m => m.text).length] })] }), _jsx("button", { className: 'ghost inv-icon', "aria-label": 'Store & camera settings', title: 'Store & camera settings', onClick: toggleSettings, children: "\u2699" }), v && _jsxs("div", { className: 'inv-menu', ref: menuRef, children: [_jsx("button", { ref: c.moreButton, className: 'ghost inv-icon', "aria-label": 'More actions', "aria-expanded": menu, onClick: () => setMenu(o => !o), children: "\u22EF" }), menu && _jsxs("div", { className: 'inv-menu-list', children: [_jsx("button", { className: 'ghost', disabled: c.busy || c.running, onClick: () => { setMenu(false); void c.mutate('archive'); }, children: v.archived_at ? 'Restore' : 'Archive' }), _jsx("button", { className: 'ghost', disabled: c.busy, onClick: () => { setMenu(false); c.setConfirmDelete(true); }, children: "Delete" })] })] })] })] });
}
// A red stop glyph (filled square in a circle), like a recording stop.
function StopButton({ c }) {
    const l = c.latest;
    if (!c.running || !l)
        return null;
    return _jsx("button", { type: 'button', className: 'inv-stop', "aria-label": 'Stop investigation', title: l.cancel_requested_at ? 'Stopping…' : 'Stop investigation', disabled: c.busy || !!l.cancel_requested_at, onClick: () => void c.mutate('cancel'), children: _jsx("span", { className: 'inv-stop-glyph', "aria-hidden": 'true' }) });
}
// The Case header's one-line conclusion: the first sentence, ellipsized; the
// whole conclusion in a popover on hover or focus, and on tap for touch.
function Conclusion({ text }) {
    // Hover, focus and a tap each open it; Escape or an outside click closes
    // all three at once. A tap pins (a touch tap also focuses, so the first tap
    // must not close what its own focus opened); the second tap closes.
    const [hover, setHover] = useState(false);
    const [focus, setFocus] = useState(false);
    const [pinned, setPinned] = useState(false);
    const ref = useRef(null);
    const id = useId();
    const open = hover || focus || pinned;
    useDismiss(open, () => { setHover(false); setFocus(false); setPinned(false); }, ref);
    if (!text)
        return null;
    return _jsxs("div", { className: 'inv-concl-line', ref: ref, onMouseEnter: () => setHover(true), onMouseLeave: () => setHover(false), children: [_jsx("button", { type: 'button', className: 'inv-concl-head', "aria-describedby": open ? id : undefined, onFocus: () => setFocus(true), onBlur: () => setFocus(false), onClick: () => { if (pinned) {
                    setHover(false);
                    setFocus(false);
                    setPinned(false);
                }
                else
                    setPinned(true); }, children: firstSentence(text, 400).head }), open && _jsx("div", { id: id, role: 'tooltip', className: 'inv-concl-pop', children: _jsx(Prose, { text: text }) })] });
}
// The server's wrap-up conclusion for the latest finished run, else the report's first paragraph.
// Messages carry no run id: drop intake questions (prompt) and anything written before the first search run.
export function caseConclusion(v) {
    const firstSearch = v.runs.find(isSearch);
    const from = firstSearch ? Date.parse(firstSearch.created_at) : Infinity;
    const searchMsgs = v.messages.filter(m => !m.prompt && !(Date.parse(m.created_at) < from));
    return [...v.runs].reverse().find(r => isSearch(r) && r.finished_at)?.conclusion || conclusionOf(searchMsgs);
}
// What the investigator is doing now, for the working indicator. Progress rows
// carry only their kind (browser.detail projects run_id, sequence, kind,
// created_at and the intake's cost/cause; tool payloads never reach the
// browser). A tool_result/tool_error row is named from `checked`, the newest
// completed tool call with its typed arguments (cameras, event_id).
const TOOL_PHRASE = { search: 'Searching clips…', timeline: 'Searching clips…', list_cameras: 'Checking the cameras…', record_finding: 'Recording a finding…' };
export function activityBlurb(v) {
    const run = v.runs.at(-1);
    if (!run || !active(run.status))
        return '';
    if (!isSearch(run))
        return [...v.progress].some(p => p.run_id === run.id && p.kind === 'window_asked') ? 'Asking for the time window…' : 'Reading the report…';
    if (run.wrap_up_state === 'pending')
        return 'Writing the conclusion…';
    const last = v.progress.filter(p => p.run_id === run.id).sort((a, b) => b.sequence - a.sequence)[0];
    if (!last || last.kind === 'window_set')
        return 'Searching clips…';
    if (last.kind === 'window_asked')
        return 'Asking for the time window…';
    const since = Date.parse(run.started_at ?? run.created_at);
    const tool = (v.checked ?? []).find(t => !(Date.parse(t.completed_at) < since));
    if (!tool)
        return 'Searching clips…';
    if (['view_frames', 'ask_clip', 'get_event'].includes(tool.tool)) {
        const named = (tool.event_id && v.sources.find(x => x.event_id === tool.event_id)?.current_name) || (tool.cameras?.length === 1 ? v.sources.find(x => x.camera === tool.cameras[0])?.current_name ?? `Camera ${tool.cameras[0]}` : '');
        return named ? `Looking at frames from ${named}…` : 'Looking at frames…';
    }
    return TOOL_PHRASE[tool.tool] ?? 'Working through the evidence…';
}
// The typing bubble at the foot of the thread while a run is active: three
// animated dots in an investigator-grey bubble, the blurb beside it. Not a
// live region: the pill's state label is the one role=status; the blurb
// changes too often to announce.
function Thinking({ text }) {
    return _jsxs("div", { className: 'inv-typing', "data-testid": 'inv-thinking', children: [_jsx("span", { className: 'inv-typing-bubble', "aria-hidden": 'true', children: _jsxs("span", { className: 'inv-dots inv-dots-anim', children: [_jsx("span", { className: 'inv-dot' }), _jsx("span", { className: 'inv-dot' }), _jsx("span", { className: 'inv-dot' })] }) }), _jsx("span", { className: 'inv-blurb', "aria-live": 'off', children: text || 'Investigator is working' })] });
}
// Conversation view: a slim row pinned above the compose box with the cost
// pill and Stop.
function StatusRow({ c }) {
    return _jsxs("div", { className: 'inv-status-row', children: [_jsx("span", { className: 'inv-spacer' }), _jsx(StatusPill, { c: c, up: true }), _jsx(StopButton, { c: c })] });
}
function StatusPill({ c, up = false }) {
    const v = c.visible;
    const l = c.latest;
    const secs = l && c.running ? Math.max(0, Math.floor((c.now - Date.parse(l.started_at ?? l.created_at)) / 1000)) : null;
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useDismiss(open, () => setOpen(false), ref);
    // The live region is the state label alone, outside the interactive summary;
    // the ticking elapsed counter is not announced.
    return _jsxs("span", { className: 'inv-pill' + (c.running ? ' live' : '') + (up ? ' up' : ''), children: [_jsx("strong", { role: 'status', children: c.awaiting ? 'Waiting for the time window' : l ? (l.final_reason === 'context_limit' ? 'Paused at its limit' : runLabel(l, c.running)) : 'Saved' }), _jsxs("details", { ref: ref, open: open, onToggle: e => setOpen(e.currentTarget.open), children: [_jsxs("summary", { "aria-label": 'Run details', children: [" \u00B7 ", secs !== null && _jsxs(_Fragment, { children: [elapsed(secs), " \u00B7 "] }), "$", directCost(v.cost).toFixed(2), " cost"] }), _jsxs("div", { className: 'inv-pill-body', children: [c.running && _jsxs("p", { children: ["Reviewed ", c.sources.length, " clips \u00B7 ", v.findings.length, " findings so far."] }), _jsxs("p", { children: ["Cost ", money(directCost(v.cost)), Number(v.cost.pending_cost ?? 0) > 0 && _jsxs(_Fragment, { children: [" (includes ", money(v.cost.pending_cost), " pending)"] }), Number(v.cost.cache_read_tokens ?? 0) > 0 && _jsxs(_Fragment, { children: [" \u00B7 Prompt cache saved ", money(v.cost.cache_saved ?? 0), " (", Number(v.cost.cache_read_tokens).toLocaleString(), " tokens reused)"] })] }), v.cost.estimated_price != null && !Number.isNaN(Number(v.cost.estimated_price)) && Number(v.cost.estimated_price).toFixed(4) !== directCost(v.cost).toFixed(4) && _jsxs("p", { children: ["Price (with markup) ", money(v.cost.estimated_price)] }), Number(v.cost.pending_cost ?? 0) > 0 && _jsx("p", { children: "Some provider usage is unsettled; this cost may change." }), _jsxs("h4", { children: ["Run progress \u00B7 ", v.progress.length, " recent updates"] }), _jsx("ol", { children: v.progress.map(p => _jsxs("li", { children: [clock(p.created_at, c.tz), " \u00B7 ", lineText(p.kind, p.cost)] }, `${p.run_id}:${p.sequence}`)) }), _jsx("h4", { children: "Checked cameras & windows" }), _jsx("ul", { children: (v.checked ?? []).map((x, i) => _jsxs("li", { children: [x.tool.replaceAll('_', ' '), x.cameras ? ` · cameras ${x.cameras.join(', ')}` : '', x.since !== undefined && x.until !== undefined ? ` · ${span(x.since, x.until, c.tz)}` : '', x.offsets_ms ? ` · inspected offsets ${x.offsets_ms.map(n => (n / 1000).toFixed(2) + 's').join(', ')}` : ''] }, i)) })] })] })] });
}
// ADR-194 Start: no window fields. The investigator asks for the time in chat
// when the report does not state it (B3).
function StartForm({ c }) {
    const starters = [...new Set(c.items.map(i => i.title.trim()).filter(Boolean))].slice(0, 4);
    return _jsxs("div", { className: 'inv-start-card', children: [_jsx("h2", { children: "What happened?" }), _jsx("p", { children: "Describe the incident the way you would tell a coworker. Include who, where, and roughly when. The investigator will ask if it needs a time window. Results are provisional until you review the source clips." }), _jsxs("form", { onSubmit: e => void c.submit(e), className: 'investigator-form', children: [c.nvrs.length > 1 && _jsxs("label", { children: ["Recorder", _jsx("select", { value: c.nvr, onChange: e => c.setNvr(e.target.value), children: c.nvrs.map(n => _jsx("option", { children: n.id }, n.id)) })] }), _jsxs("label", { children: ["Incident report", _jsx("textarea", { required: true, maxLength: 20000, rows: 6, value: c.report, onChange: e => c.setReport(e.target.value), placeholder: 'What happened? Include people, locations and roughly when.' })] }), starters.length > 0 && _jsxs("div", { className: 'inv-starters', children: [_jsx("small", { className: 'inv-hint', children: "Start from a past case" }), _jsx("div", { className: 'inv-picks', children: starters.map(t => _jsx("button", { type: 'button', className: 'inv-pick', onClick: () => c.setReport(t + ': '), children: t }, t)) })] }), _jsxs("div", { className: 'inv-start-foot', children: [c.zone && _jsxs("small", { className: 'inv-hint', children: ["Times are store local \u00B7 ", c.zone] }), _jsx("span", { className: 'inv-spacer' }), _jsx("button", { className: 'primary', disabled: c.busy || !c.nvr || !c.metadataLoaded || !c.report.trim(), children: "Start investigation" })] })] })] });
}
// The clarifying turn: one chip per server-built option, plus "Pick a time…"
// for local From/Until (offset fields only after a DST rejection).
function WindowQuestion({ c, prompt }) {
    const [custom, setCustom] = useState(false);
    const blocked = c.busy || c.tzChanged || !!c.visible?.archived_at;
    const offsets = c.needsOffset;
    const send = (e) => {
        e.preventDefault();
        if (blocked || !c.since || !c.until)
            return;
        void c.answerWindow({ since: c.since, until: c.until, ...(c.fromOffset !== '' ? { since_offset: Number(c.fromOffset) } : {}), ...(c.toOffset !== '' ? { until_offset: Number(c.toOffset) } : {}) });
    };
    return _jsxs("div", { className: 'inv-question', children: [_jsxs("div", { className: 'inv-picks', role: 'group', "aria-label": 'Time window choices', children: [prompt.options.map(o => _jsx("button", { type: 'button', className: 'inv-pick acc', disabled: blocked, onClick: () => void c.answerWindow({ option_id: o.id }), children: chipLabel(o, c.tz) }, o.id)), prompt.allow_custom && _jsx("button", { type: 'button', className: 'inv-pick', "aria-expanded": custom, disabled: blocked, onClick: () => setCustom(o => !o), children: "Pick a time\u2026" })] }), custom && _jsxs("form", { className: 'inv-question-form', onSubmit: send, children: [_jsxs("div", { className: 'investigator-row', children: [_jsxs("label", { children: ["From", _jsx("input", { disabled: blocked, type: 'datetime-local', required: true, value: c.since, onChange: e => { c.setSince(e.target.value); c.invalidate(); } })] }), _jsxs("label", { children: ["Until", _jsx("input", { disabled: blocked, type: 'datetime-local', required: true, value: c.until, onChange: e => { c.setUntil(e.target.value); c.invalidate(); } })] })] }), offsets && _jsxs(_Fragment, { children: [_jsxs("p", { className: 'investigator-error', role: 'status', children: ["This local time is ambiguous or does not exist because of daylight saving. Enter its UTC offset in minutes", offsets.length ? ` (${offsets.join(' or ')})` : '', " for the From and Until times."] }), _jsxs("details", { open: true, children: [_jsx("summary", { children: "Daylight-saving offset" }), _jsxs("div", { className: 'investigator-row', children: [_jsxs("label", { children: ["From offset", _jsx("input", { disabled: blocked, type: 'number', min: -1440, max: 1440, value: c.fromOffset, onChange: e => c.setFromOffset(e.target.value) })] }), _jsxs("label", { children: ["Until offset", _jsx("input", { disabled: blocked, type: 'number', min: -1440, max: 1440, value: c.toOffset, onChange: e => c.setToOffset(e.target.value) })] })] })] })] }), _jsxs("small", { className: 'inv-hint', children: ["Times are store local", c.tz ? ` · ${c.tz}` : ''] }), _jsx("button", { className: 'primary', disabled: blocked || !c.since || !c.until, children: "Use this time" })] })] });
}
// The server label is "<name>: <24-hour span>"; the chip rebuilds the span in
// 12-hour store time from the option's own bounds. A label carrying a DST
// offset (or no name) stays as the server wrote it.
export const chipLabel = (o, tz) => { const i = o.label.indexOf(': '); return i < 0 || /\(UTC/.test(o.label) ? o.label : `${o.label.slice(0, i)} (${span(o.since, o.until, tz)})`; };
// One rail row (or conversation card): camera · time, then the investigator's
// own finding sentence for the clip with its confidence in words. A clip with
// no finding shows the camera's own description, muted. Ruled-out clips stay
// in time order, dimmed and tagged.
function ClipCard({ c, s, row = false, day = false }) {
    const [broken, setBroken] = useState(false);
    const v = c.visible;
    const f = (c.findingsBySource.get(s.id) ?? [])[0];
    const at = f?.citations.find(x => x.source_id === s.id);
    const ruled = f?.category === 'ruled_out';
    const on = c.selection?.source === s.id;
    return _jsxs("button", { className: (row ? 'inv-row' : 'inv-card') + (on ? ' on' : '') + (s.hidden ? ' inv-hidden' : '') + (ruled ? ' inv-ruled' : ''), "aria-current": on ? 'true' : undefined, onClick: () => c.choose(s.id, (at?.offset_ms ?? 0) / 1000), children: [_jsxs("span", { className: 'inv-thumb', children: [s.media_available && v.window && !broken && _jsx("img", { src: evidenceUrl({ investigation: v.id, source: s.id, offset: 0 }, 'thumb'), alt: '', loading: 'lazy', onError: () => setBroken(true) }), s.hidden && !row && _jsx("span", { className: 'inv-hidden-tag', children: "hidden" })] }), _jsxs("span", { className: 'inv-card-text', children: [_jsxs("span", { className: 'investigator-reel-when', children: [day && _jsxs("b", { className: 'inv-day', children: [dayOf(s.event_start, c.tz), " \u00B7 "] }), s.current_name, " \u00B7 ", zoneTime(s.event_start, c.tz), !s.media_available ? ' · video unavailable' : ''] }), f ? _jsxs("span", { className: 'investigator-reel-summary', children: [ruled && _jsx("span", { className: 'inv-ruled-tag', children: "Ruled out" }), firstSentence(f.claim, row ? 140 : 90).head, " ", _jsxs("span", { className: 'inv-conf', children: ["\u00B7 ", confWord(f.confidence)] })] }) :
                        _jsx("span", { className: 'investigator-reel-summary inv-auto-line', children: oneLine(clipSummary(s.description), row ? 110 : 80) })] })] });
}
// The card under the video: the finding in one sentence, then camera · time ·
// confidence in words, the feedback row, and one link to the camera's own
// description. Nothing else expands.
function WhyCard({ c }) {
    const s = c.selected;
    const [desc, setDesc] = useState(false);
    if (!s)
        return null;
    const fs = c.findingsBySource.get(s.id) ?? [];
    const f = fs[0];
    const text = cameraDescription(s.description);
    return _jsxs("div", { className: 'inv-why', children: [_jsx("p", { className: 'inv-why-claim', children: f ? firstSentence(f.claim, 240).head : NO_FINDING }), fs.slice(1, 3).map(x => _jsx("p", { className: 'inv-why-also', children: firstSentence(x.claim, 160).head }, `${x.id}:${x.version}`)), _jsxs("p", { className: 'inv-why-meta', children: [s.current_name, " \u00B7 ", zoneTime(s.event_start, c.tz), f && _jsxs(_Fragment, { children: [" \u00B7 Investigator is ", confWord(f.confidence)] }), f?.category === 'ruled_out' && _jsx("span", { className: 'inv-ruled-tag', children: "Ruled out" })] }), c.selection && !c.loadingClip && _jsx(Feedback, { c: c, s: s }, s.id), text && _jsx("button", { type: 'button', className: 'inv-link', "aria-expanded": desc, onClick: () => setDesc(o => !o), children: "Camera's own description" }), desc && text && _jsx("p", { className: 'inv-why-desc', children: text })] });
}
// Feedback lives only under the video, never on rail rows (ADR-194).
function Feedback({ c, s }) {
    const v = c.visible;
    const verdict = s.feedback?.verdict ?? null;
    const saved = s.feedback?.note ?? '';
    const [open, setOpen] = useState(false);
    const [note, setNote] = useState(saved);
    const [said, setSaid] = useState('');
    const blocked = c.running || !!v.archived_at;
    const claim = (c.findingsBySource.get(s.id) ?? [])[0]?.claim;
    const thumb = (t) => { const next = verdict === t ? null : t; setSaid(next === 'down' ? 'Clip hidden from evidence; find it under other clips.' : ''); void c.sendFeedback(s, next, s.feedback?.note ?? null); };
    return _jsxs("div", { className: 'inv-fb', children: [_jsxs("div", { className: 'inv-fb-row', children: [_jsx("button", { className: 'ghost' + (verdict === 'up' ? ' on' : ''), "aria-pressed": verdict === 'up', "aria-label": 'Mark clip relevant', onClick: () => thumb('up'), children: "\uD83D\uDC4D" }), _jsx("button", { className: 'ghost' + (verdict === 'down' ? ' on' : ''), "aria-pressed": verdict === 'down', "aria-label": 'Mark clip not relevant', onClick: () => thumb('down'), children: "\uD83D\uDC4E" }), _jsx("button", { className: 'ghost' + (saved ? ' on' : ''), "aria-label": 'Note', "aria-expanded": open, onClick: () => { setNote(saved); setOpen(o => !o); }, children: "\uD83D\uDCAC Note" }), _jsx("span", { className: 'inv-spacer' }), _jsx("button", { className: 'ghost', disabled: blocked, onClick: () => c.askAbout(`About the ${s.current_name} clip at ${zoneTime(s.event_start, c.tz)}: `), children: "Ask about this clip" }), _jsx("button", { className: 'ghost', disabled: blocked || c.busy || c.pendingRun !== null, onClick: () => void c.submit(null, true, `Look closer at ${s.current_name} around ${zoneSec(s.event_start, c.tz)}${claim ? `; ${claim}` : '.'}`), children: "Dig deeper" })] }), _jsx("p", { role: 'status', className: 'inv-sr', children: said }), open && _jsxs("form", { className: 'inv-fb-note', onSubmit: e => { e.preventDefault(); void c.sendFeedback(s, verdict, note.trim() || null).then(ok => { if (ok)
                    setOpen(false); }); }, children: [_jsxs("label", { children: [_jsx("span", { className: 'inv-sr', children: "Note on this clip" }), _jsx("textarea", { rows: 2, maxLength: 2000, value: note, onChange: e => setNote(e.target.value), placeholder: 'What should later readers know about this clip?' })] }), _jsx("button", { className: 'primary', children: "Save note" })] })] });
}
function OffPage({ c }) {
    if (!c.offPage.length)
        return null;
    return _jsxs("div", { className: 'inv-why', children: [_jsx("h4", { children: "Findings citing clips outside this page" }), c.offPage.map((f) => _jsxs("div", { className: 'investigator-finding', children: [_jsxs("p", { children: [firstSentence(f.claim, 240).head, " ", _jsxs("span", { className: 'inv-conf', children: ["\u00B7 ", confWord(f.confidence)] })] }), _jsx("div", { className: 'investigator-actions', children: f.citations.map(x => _jsxs("button", { className: 'ghost', onClick: () => c.choose(x.source_id, (x.offset_ms ?? 0) / 1000), children: ["Open cited clip \u00B7 ", ((x.offset_ms ?? 0) / 1000).toFixed(2), "s"] }, `${x.source_id}:${x.offset_ms}`)) })] }, `${f.id}:${f.version}`))] });
}
// Seek-bar ticks for the open clip (ADR-194): offsets the investigator inspected
// on this event, plus this clip's citations that carry an offset. One tick per whole second; a cited
// tick wins over an inspected one at the same second. The textual list stays in
// the "Checked cameras & windows" details.
export function stageMarkers(checked, findings, s) {
    const out = new Map();
    for (const f of findings)
        for (const x of f.citations)
            if (x.source_id === s.id && x.offset_ms != null) {
                const at = x.offset_ms / 1000;
                const k = Math.floor(at);
                if (out.get(k)?.kind !== 'cited')
                    out.set(k, { atSec: at, label: f.claim.length > 40 ? f.claim.slice(0, 39) + '…' : f.claim || 'Finding', kind: 'cited' });
            }
    for (const x of checked ?? [])
        if (x.event_id === s.event_id)
            for (const ms of x.offsets_ms ?? []) {
                const k = Math.floor(ms / 1000);
                if (!out.has(k))
                    out.set(k, { atSec: ms / 1000, label: 'Inspected frame', kind: 'inspected' });
            }
    return [...out.values()].sort((a, b) => a.atSec - b.atSec);
}
function Stage({ c, play = false, ticks = false }) {
    const v = c.visible;
    const s = c.selected;
    const sources = c.playlist;
    const index = c.index;
    const prev = index >= 0 ? index - 1 : c.virtual - 1;
    const next = index >= 0 ? index + 1 : c.virtual >= 0 ? c.virtual : sources.length;
    const markers = ticks && s ? stageMarkers(v.checked ?? [], c.findingsBySource.get(s.id) ?? [], s) : [];
    // Case view only (ticks): trim to the detections unless full clips are on.
    // Start and end come from ONE marker set per player key, computed from the
    // same snapshot the clip renders from, and only once the clip itself is
    // known (a clip still resolving through the single-source route has no
    // markers yet; capturing then would switch trimming off for good). A live
    // snapshot adding a marker later cannot move a captured window. One
    // exception: a clip captured with NO markers adopts the first non-empty set
    // once, for its end only; the start is not re-applied, because seeking a
    // clip that is already playing would yank the picture. The ticks themselves
    // stay live. The toggle applies at once.
    const captured = useRef({ key: '', markers: null, late: false });
    if (captured.current.key !== c.keyNow)
        captured.current = { key: c.keyNow, markers: s ? markers : null, late: false };
    else if (s && captured.current.markers === null)
        captured.current.markers = markers;
    else if (s && captured.current.markers?.length === 0 && markers.length && !captured.current.late)
        captured.current = { key: c.keyNow, markers, late: true };
    const window_ = ticks && !c.fullClips ? trimWindow(captured.current.markers ?? []) : null;
    const trim = window_ && { start: captured.current.late ? 0 : window_.start, end: window_.end };
    return _jsxs("section", { className: 'investigator-stage', "aria-label": 'Evidence playback', children: [_jsxs("div", { className: 'investigator-stage-bar', children: [_jsx("h3", { children: s ? `${s.current_name} · ${zoneSec(s.event_start, c.tz)}` : 'Evidence' }), _jsx("span", { className: 'inv-spacer' }), play && _jsx("button", { className: 'primary', disabled: c.busy || !sources.some(x => x.media_available), onClick: c.playAll, children: c.playing ? 'Restart' : 'Play all' }), _jsx("button", { className: 'ghost', disabled: prev < 0, onClick: () => c.step(prev, false), "aria-label": 'Previous clip', children: "\u2039" }), _jsx("span", { className: 'investigator-stage-count', children: index >= 0 ? `Clip ${index + 1} of ${sources.length}` : c.virtual >= 0 ? `Hidden clip · ${clips(sources.length)}` : clips(sources.length) }), _jsx("button", { className: 'ghost', disabled: next >= sources.length, onClick: () => c.step(next, false), "aria-label": 'Next clip', children: "\u203A" }), ticks && _jsxs("label", { className: 'investigator-check inv-full', title: 'Off: each clip starts 3 s before its first detection and moves on 3 s after its last (clips under 20 s play whole).', children: [_jsx("input", { type: 'checkbox', checked: c.fullClips, onChange: e => c.setFullClips(e.target.checked) }), " Play full clips"] })] }), c.loadingClip ? _jsx("div", { className: 'investigator-stage-empty', role: 'status', children: "Loading selected evidence\u2026" }) :
                !c.selection || !s ? _jsx("div", { className: 'investigator-stage-empty', role: 'status', children: sources.length ? 'No clip open. Choose a clip, or press Play all to walk through them in order.' : 'No clips yet.' }) :
                    !s.media_available || !v.window ? _jsx("div", { className: 'investigator-stage-empty', role: 'status', children: "Video unavailable for this clip. Its findings remain below; choose another clip to continue." }) :
                        _jsx(ClipPlayer, { ev: null, mode: 'inline', evidence: c.selection, evidenceLabel: { camera: s.current_name, time: zoneSec(s.event_start, c.tz) }, onEnded: c.advance, playToken: c.playToken, onPlayingChange: v => c.onPlayingChange(c.keyNow, v), onScrubChange: v => c.onScrubChange(c.keyNow, v), markers: markers, trimStartSec: trim?.start ?? 0, onTimeUpdate: (sec, d) => c.onTimeUpdate(c.keyNow, sec, d, trim?.end ?? null) }), _jsx(WhyCard, { c: c }, s?.id), _jsx(OffPage, { c: c })] });
}
function RunNotice({ c }) {
    const v = c.visible;
    const l = c.latest;
    return _jsxs(_Fragment, { children: [c.running && _jsx("p", { className: 'inv-hint', children: "Clips appear as they are inspected. Play them now, and stop the investigation once you find what you need; everything found so far is kept." }), l?.status === 'queued' && _jsx("p", { className: 'inv-hint', children: "Waiting for worker capacity. Queue time depends on active investigations." }), l?.final_reason === 'context_limit' && _jsxs("p", { className: 'inv-notice', children: ["The investigation reached its working limit and wrote up what it found so far. ", _jsx("button", { className: 'primary', disabled: c.busy || !!v.archived_at, onClick: () => void c.submit(null, true, KEEP_GOING), children: "Keep going" })] }), l && !c.running && l.status !== 'completed' && _jsx("p", { className: 'inv-notice', children: "This run stopped early. Existing findings may be incomplete. Ask a follow-up to retry with a fresh run." })] });
}
function FollowUp({ c }) {
    const v = c.visible;
    const blocked = c.running || !!v.archived_at || c.awaiting;
    return _jsxs("form", { className: 'inv-compose', onSubmit: e => void c.submit(e, true), children: [_jsxs("label", { children: [_jsx("span", { className: 'inv-sr', children: "Follow-up" }), _jsx("textarea", { ref: c.composeRef, rows: 2, maxLength: 20000, value: c.followup, placeholder: 'Steer or ask a follow-up\u2026', onChange: e => c.setFollowup(e.target.value), disabled: blocked })] }), _jsx("button", { className: 'primary', disabled: c.busy || blocked || !c.followup.trim(), children: "Ask follow-up" }), blocked && _jsx("small", { className: 'inv-hint', children: c.awaiting ? 'Answer the time question above.' : v.archived_at ? 'Restore this investigation to ask a follow-up.' : 'You can ask a follow-up when this run finishes. Stop it to change direction now.' })] });
}
// Investigator bubbles read as short chat: a long one is capped at ~12 lines
// with Show more. The line count is an estimate (jsdom and print cannot measure).
const BUBBLE_LINES = 12;
const estLines = (text) => text.split('\n').reduce((n, l) => n + Math.max(1, Math.ceil(l.length / 72)), 0);
function Bubble({ text, ai }) {
    const [open, setOpen] = useState(false);
    const long = ai && estLines(text) > BUBBLE_LINES;
    if (!ai)
        return _jsx("p", { children: text });
    return _jsxs(_Fragment, { children: [_jsx("div", { className: long && !open ? 'inv-clamp' : undefined, children: _jsx(Prose, { text: text }) }), long && _jsx("button", { type: 'button', className: 'ghost inv-expand', "aria-expanded": open, onClick: () => setOpen(o => !o), children: open ? 'Show less' : 'Show more' })] });
}
// The thread scrolls in its own pane and follows the bottom while the reader
// is within FOLLOW_SLACK px of it; scrolling up stops following, and new
// content then raises "Jump to latest".
const FOLLOW_SLACK = 80;
// "Jump to latest" shows whenever the reader is not following AND the thread
// grew since they scrolled up; it is re-evaluated on every snapshot and on any
// resize of the thread's content (images, Show more, late layout).
function useFollow(signature, page) {
    const ref = useRef(null);
    const follow = useRef(true);
    const [unseen, setUnseen] = useState(false);
    const lastPage = useRef(page);
    const leftAt = useRef(0);
    const toBottom = () => { const el = ref.current; if (el)
        el.scrollTop = el.scrollHeight; };
    const check = () => { const el = ref.current; if (!el)
        return; if (follow.current)
        toBottom();
    else if (el.scrollHeight > leftAt.current)
        setUnseen(true); };
    const onScroll = () => {
        const el = ref.current;
        if (!el)
            return;
        const was = follow.current;
        follow.current = el.scrollHeight - el.scrollTop - el.clientHeight <= FOLLOW_SLACK;
        if (follow.current)
            setUnseen(false);
        else if (was)
            leftAt.current = el.scrollHeight;
    };
    // Paging is the reader's own move, not news: it resets to following.
    useLayoutEffect(() => {
        if (lastPage.current !== page) {
            lastPage.current = page;
            follow.current = true;
            setUnseen(false);
        }
        check();
    }, [signature, page]);
    const checkRef = useRef(check);
    checkRef.current = check;
    useEffect(() => {
        const el = ref.current;
        if (!el || typeof ResizeObserver === 'undefined')
            return;
        const ro = new ResizeObserver(() => checkRef.current());
        ro.observe(el);
        for (const child of el.children)
            ro.observe(child);
        return () => ro.disconnect();
    }, [signature, page]);
    const jump = () => { follow.current = true; setUnseen(false); toBottom(); };
    return { ref, onScroll, unseen, jump };
}
// The conversation column fills the viewport below everything above it, so
// the thread scrolls inside and the compose box never leaves the screen.
// --inv-conv-top is the column's document offset, re-measured whenever the
// app bar or investigation header changes size.
function useFillViewport() {
    const ref = useRef(null);
    useLayoutEffect(() => {
        const el = ref.current;
        if (!el)
            return;
        const measure = () => el.style.setProperty('--inv-conv-top', `${Math.round(el.getBoundingClientRect().top + window.scrollY)}px`);
        measure();
        window.addEventListener('resize', measure);
        const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
        for (const above of document.querySelectorAll('header.appbar,.inv-head,.investigator-main'))
            ro?.observe(above);
        return () => { window.removeEventListener('resize', measure); ro?.disconnect(); };
    }, []);
    return ref;
}
function WorkingView({ c }) {
    const v = c.visible;
    const tz = c.tz;
    const msgs = v.messages.filter(m => m.text || m.prompt);
    // Only the newest question is answerable, and only while the window is unset.
    const ask = c.awaiting ? [...msgs].reverse().find(m => m.prompt) : undefined;
    // Homes come from attachCards (hook effect). A cited clip whose home is not
    // on this message page, or that has no home yet, waits in the pending row.
    const home = c.cardHome;
    const shown = new Set(msgs.map(m => m.id));
    // History stays put: a card hidden after attaching remains in its bubble, dimmed.
    const cardsFor = (messageId) => c.sources.filter(s => c.findingsBySource.has(s.id) && home[s.id] === messageId).sort(byStart);
    const pending = c.rail.main.filter(s => !home[s.id] || !shown.has(home[s.id]));
    const progress = [...v.progress].sort((a, b) => Date.parse(a.created_at) - Date.parse(b.created_at));
    const lines = [];
    for (const p of progress) {
        const last = lines.at(-1);
        if (last && last.kind === p.kind && last.run === p.run_id)
            last.count++;
        else
            lines.push({ at: p.created_at, kind: p.kind, run: p.run_id, count: 1, text: lineText(p.kind, p.cost) });
    }
    // An intake that failed operationally still asks; the cause stays internal.
    for (const r of v.runs)
        if (r.final_reason === 'needs_window_unavailable')
            lines.push({ at: r.finished_at ?? r.created_at, kind: 'needs_window_unavailable', run: r.id, count: 1, text: UNREADABLE_WINDOW });
    const timeline = [...msgs.map(m => ({ at: m.created_at, m })), ...lines.map(g => ({ at: g.at, g }))].sort((a, b) => Date.parse(a.at) - Date.parse(b.at));
    const lastMsg = msgs.at(-1);
    const column = useFillViewport();
    const pane = useFollow(`${msgs.length}:${progress.length}:${progress.at(-1)?.created_at}:${progress.at(-1)?.sequence}:${lines.length}:${lastMsg?.id}:${lastMsg?.text?.length}:${pending.length}`, c.messagePage);
    return _jsxs("div", { className: 'inv-working', children: [_jsxs("section", { className: 'inv-conv', "aria-label": 'Conversation', ref: column, children: [_jsxs("div", { className: 'investigator-actions', children: [_jsx("button", { className: 'ghost', disabled: !v.pages?.messages_more, onClick: () => c.setMessagePage(p => p + 1), children: "Older messages" }), _jsx("button", { className: 'ghost', disabled: c.messagePage === 0, onClick: () => c.setMessagePage(p => p - 1), children: "Newer messages" })] }), _jsxs("div", { className: 'inv-thread', children: [_jsxs("div", { className: 'inv-msgs', ref: pane.ref, onScroll: pane.onScroll, children: [timeline.map(t => 'm' in t ? _jsxs("article", { className: 'inv-bubble ' + (t.m.role === 'user' ? 'me' : 'ai'), children: [_jsxs("span", { className: 'inv-who', children: [t.m.role === 'user' ? 'You' : 'Investigator', " \u00B7 ", zoneTime(t.m.created_at, tz)] }), t.m.text ? _jsx(Bubble, { text: t.m.text, ai: t.m.role !== 'user' }) : t.m.prompt && _jsx("p", { children: "When should I look?" }), t.m === ask && t.m.prompt && _jsx(WindowQuestion, { c: c, prompt: t.m.prompt }), cardsFor(t.m.id).length > 0 && _jsx("div", { className: 'inv-clips', children: cardsFor(t.m.id).map(s => _jsx(ClipCard, { c: c, s: s }, s.id)) })] }, t.m.id) : _jsxs("p", { className: 'inv-sys', children: [t.g.text, t.g.count > 1 ? ` ×${t.g.count}` : ''] }, `${t.g.run}:${t.at}:${t.g.kind}`)), !msgs.length && c.running && _jsx("p", { className: 'inv-sys', children: "Investigating \u2014 reviewing cameras and clips as they are found\u2026" }), pending.length > 0 && _jsx("div", { className: 'inv-clips inv-pending', "aria-label": 'Clips found so far', children: pending.map(s => _jsx(ClipCard, { c: c, s: s }, s.id)) }), c.running && _jsx(Thinking, { text: activityBlurb(v) })] }), pane.unseen && _jsx("button", { type: 'button', className: 'inv-jump', onClick: pane.jump, children: "Jump to latest \u2193" })] }), c.tzChanged && _jsxs("p", { className: 'inv-notice', role: 'alert', children: ["The store timezone changed. Start a new investigation. ", _jsx("button", { className: 'primary', onClick: c.restart, children: "Start a new investigation" })] }), _jsx(RunNotice, { c: c }), _jsx(StatusRow, { c: c }), _jsx(FollowUp, { c: c })] }), _jsx("aside", { className: 'inv-view', children: _jsx(Stage, { c: c, play: true }) })] });
}
function CaseView({ c }) {
    const v = c.visible;
    const { main, other } = c.rail;
    // The rail timeline carries the date: a "Tue Oct 6" prefix on each day's first row.
    const newDay = (list, i) => i === 0 || dayOf(list[i].event_start, c.tz) !== dayOf(list[i - 1].event_start, c.tz);
    return _jsxs("div", { className: 'inv-case', children: [_jsxs("div", { className: 'inv-case-main', children: [_jsx(Stage, { c: c, ticks: true }), _jsx(RunNotice, { c: c }), _jsx(FollowUp, { c: c })] }), _jsxs("aside", { className: 'investigator-reel inv-rail', "aria-label": 'Evidence', children: [_jsxs("div", { className: 'inv-rail-top', children: [_jsx("h3", { children: "Evidence" }), _jsx("small", { children: clips(main.length) }), _jsx("span", { className: 'inv-spacer' }), _jsx("button", { className: 'primary', disabled: c.busy || !c.playlist.some(s => s.media_available), onClick: c.playAll, children: c.playing ? 'Restart' : 'Play all' })] }), _jsx("ol", { children: main.map((s, i) => _jsx("li", { children: _jsx(ClipCard, { c: c, s: s, row: true, day: newDay(main, i) }) }, s.id)) }), c.includeUncited && other.length > 0 && _jsxs("section", { className: 'inv-group inv-muted', "aria-label": 'Other reviewed clips', children: [_jsx("h4", { children: "Other reviewed clips" }), _jsx("ol", { children: other.map((s, i) => _jsx("li", { children: _jsx(ClipCard, { c: c, s: s, row: true, day: newDay(other, i) }) }, s.id)) })] }), !c.sources.length && _jsx("p", { children: "No clips to show." }), _jsxs("label", { className: 'investigator-check inv-more', children: [_jsx("input", { type: 'checkbox', checked: c.includeUncited, onChange: e => { c.setIncludeUncited(e.target.checked); c.setSourcePage(0); } }), " ", other.length ? `Show the ${other.length} other clip${other.length === 1 ? '' : 's'} the investigator reviewed` : 'Show the other clips the investigator reviewed'] }), _jsxs("div", { className: 'investigator-actions', children: [_jsx("button", { className: 'ghost', disabled: c.sourcePage === 0, onClick: () => c.setSourcePage(p => p - 1), children: "Earlier clips" }), _jsx("button", { className: 'ghost', disabled: !v.pages?.sources_more, onClick: () => c.setSourcePage(p => p + 1), children: "Later clips" })] })] })] });
}
